import { Auth0Context, RedirectLoginOptions } from '@auth0/auth0-react';
import React, { useContext, useMemo } from 'react';

import {
  hasLoginRedirectStarted,
  markLoginRedirectStarted,
  resetLoginRedirectGuard
} from './loginRedirectGuard';

/**
 * Re-provides the Auth0 context with a `loginWithRedirect` that can start at
 * most ONE `/authorize` navigation per page visit (NCP-5357) — see
 * `loginRedirectGuard` for why a second one costs the customer their OTP.
 *
 * The guard is claimed at CALL time, not at mount time. A component that
 * mounts and is torn down before it ever asks to log in must not consume it,
 * or the next mount would sit on the loading state forever with no redirect
 * ever fired. That timing is why this wraps the call itself instead of living
 * in `protected-route`: only the caller knows a navigation actually began.
 *
 * Wrapping the context rather than each call site also means every consumer of
 * `useAuth0()` is covered — `withAuthenticationRequired` inside
 * `ProtectedRoute`, the Login page button, and anything added later.
 */
const SingleLoginRedirect: React.FC = ({ children }) => {
  const auth0 = useContext(Auth0Context);
  const { loginWithRedirect } = auth0;

  const guardedAuth0 = useMemo(
    () => ({
      ...auth0,
      loginWithRedirect: async (
        options?: RedirectLoginOptions
      ): Promise<void> => {
        if (hasLoginRedirectStarted()) {
          return;
        }
        // Claimed BEFORE awaiting: loginWithRedirect builds the authorize URL
        // asynchronously, so a remount can reach this line while the first
        // call is still in flight.
        markLoginRedirectStarted();
        try {
          await loginWithRedirect(options);
        } catch (error) {
          // Nothing navigated, so a retry has to be allowed.
          resetLoginRedirectGuard();
          throw error;
        }
      }
    }),
    [auth0, loginWithRedirect]
  );

  return (
    <Auth0Context.Provider value={guardedAuth0}>
      {children}
    </Auth0Context.Provider>
  );
};

export default SingleLoginRedirect;
